/**
 * Single source of truth for a video model's user-facing display name.
 *
 * BYOM ids (`gateway/byom/<uuid>`) resolve to the user-given `key_name`,
 * falling back to a truncated uuid when the model is unknown/deleted or the
 * `useVideoModels()` list has not loaded yet. Native ids resolve through the
 * static alias map (`getVideoModelLabel`).
 *
 * Used by the collapsed node Config summary (VideoGeneratorNode), the bottom
 * config bar (VideoConfigTabContent), and the "MY VIDEO MODEL" selector chips
 * (VideoConfigFlyout) so all three surfaces provably agree — previously the
 * collapsed node showed the raw `gateway/byom/<uuid>` id (post 1567479545009343).
 *
 * NOTE: lives here rather than in `videoConstraints.ts` on purpose — that module
 * is documented "pure functions — no React/hooks" and `getVideoModelLabel`
 * transitively imports `utils/debugLog` (yjs + react), which would pollute it.
 */
import type { ByomModelInfo } from '../../../../api/client'
import { getVideoModelLabel } from '../../../../constants/modelAliases'
import { createNadDebug } from '../../../../utils/debugLog'
import { isByomModel } from './videoConstraints'

// Feature tag `dw:byom-name` — traceable via `nad trace --feature byom-name`.
const byomNameResolve = createNadDebug('dw:byom-name:resolve')
const byomNameMiss = createNadDebug('dw:byom-name:miss')

/** Truncated uuid fallback shown when a BYOM `key_name` is unavailable. */
function truncatedByomId(id: string): string {
  return id.replace('gateway/byom/', '').slice(0, 8)
}

/**
 * Resolve a video model id to its display name.
 *
 * Fallback uses `||` (not `??`) so an empty-string `key_name` also degrades to
 * the truncated uuid — a truncated id is better UX than a blank label.
 */
export function getVideoModelDisplayName(id: string, byomModels: ByomModelInfo[]): string {
  if (isByomModel(id)) {
    const keyName = byomModels.find((b) => b.id === id)?.key_name
    if (keyName) {
      byomNameResolve({ event: 'byom:resolved', trace_id: id, model_id: id, key_name: keyName })
      return keyName
    }
    // Model deleted/unknown, or the video-models list has not loaded yet.
    byomNameMiss({ event: 'byom:fallback', trace_id: id, model_id: id, byom_count: byomModels.length })
    return truncatedByomId(id)
  }
  return getVideoModelLabel(id)
}
